import React, { useContext } from 'react';
import ProductPicker from '../../../components/common/ProductPicker.js'
import { useRouter } from 'next/router'
import Head from 'next/head'
import AppContext from '../../../components/common/AppContext'
import { getPickerSeo, pickerJsonLd } from '../../../utils/pickerSeo'
import {
  fetchTopicQuestions,
  fetchPublishedPickerSlugs,
  isKnownPickerSlug,
} from '../../../utils/fetchTopicQuestions'

const NOINDEX_PICKERS = new Set(['barbell', 'tv'])
const REVALIDATE_SECONDS = 60 * 60 // 1 hour — HTML stays warm on the CDN

function ProductPickerComponent(props) {
  const router = useRouter()
  const slug = props.slug || router.query.slug
  const { title, description, url, image } = getPickerSeo(slug)
  const useAppContext = () => useContext(AppContext);
  const { logged_in, handlelogin } = useAppContext();
  const hideFromBots = NOINDEX_PICKERS.has(slug)
  const jsonLd = hideFromBots ? null : pickerJsonLd(slug)

  // Avoid hydration mismatch while fallback: 'blocking' resolves a new path.
  if (router.isFallback) {
    return (
      <div>
        <Head>
          <title>{title}</title>
          <meta name="description" content={description} />
        </Head>
        <div className="simplybuy-container no-padding-right">
          <div className="spinner-container">
            <div className="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
          </div>
        </div>
      </div>
    )
  }

  return (
      <div>
      <Head>
        <title>{title}</title>
        <meta name='description' content={description}/>
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={image} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {hideFromBots && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        {jsonLd && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        )}
      </Head>
        <ProductPicker
          key={slug}
          handlelogin={handlelogin}
          logged_in={logged_in}
          router={router}
          slug={slug}
          question={props.question || router.query.question || '0'}
          initialQuestions={props.initialQuestions || null}
          seoDescription={description}
        />
      </div>
  )
}

export async function getStaticPaths() {
  const slugs = await fetchPublishedPickerSlugs()
  // Prebuild landing (/0) and first question (/1) so Start has no blocking delay.
  // Later questions still use fallback: 'blocking'.
  const paths = slugs.flatMap((slug) => [
    { params: { slug, question: '0' } },
    { params: { slug, question: '1' } },
  ])
  return {
    paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps(context) {
  const slug = context.params?.slug
  const question = context.params?.question || '0'

  if (!slug) {
    return { notFound: true }
  }

  // Retired picker: still build a page so redirects/bookmarks work, but noindex.
  if (NOINDEX_PICKERS.has(slug)) {
    return {
      props: {
        slug,
        question,
        initialQuestions: null,
      },
      revalidate: REVALIDATE_SECONDS,
    }
  }

  const initialQuestions = await fetchTopicQuestions(slug)
  if (!initialQuestions) {
    // API timeouts / 5xx must not hard-404 known pickers — ProductPicker
    // live-fetches questions on the client. Only unknown slugs are notFound.
    if (isKnownPickerSlug(slug)) {
      return {
        props: {
          slug,
          question,
          initialQuestions: null,
        },
        revalidate: 60,
      }
    }
    return { notFound: true, revalidate: 60 }
  }

  return {
    props: {
      slug,
      question,
      initialQuestions,
    },
    revalidate: REVALIDATE_SECONDS,
  }
}

export default ProductPickerComponent
